import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Api, PAYMENT_SERVICE } from '../../api/api';
import { ModalController } from '@ionic/angular';
import Payment from '../../models/Payment';
import { environment } from '../../../environments/environment';
import { PaymentMethod } from '../../enums/PaymentMethod';
import Venue from '../../models/Venue';
import Order from '../../models/Order';
import { PaymentStatus } from '../../models/PaymentStatus';
import Customer from '../../models/Customer';
import { OrderUtils } from '../../utils/order-utils';
import { MatSnackBar } from '@angular/material/snack-bar';
import { paymentFromOrders, sleep } from 'src/smoothr-web-app-core/utils/utils';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { TranslateService } from '@ngx-translate/core';
import { AdyenCheckout, CustomCard, GooglePay } from '@adyen/adyen-web';
import { AppComponent } from 'src/app/app.component';
import { paymentBody } from 'src/smoothr-web-app-core/models/Converter';
import * as Sentry from '@sentry/angular';

import { NavigationService } from 'src/app/services/navigation.service';
export enum PaymentModalResult {
	SUCCESS,
	ERROR,
	DISMISS,
}

// tslint:disable-next-line:prefer-const
declare var wpwlOptions: any;
// @ts-ignore
const applePayAvailable = !!window.ApplePaySession;

@Component({
	selector: 'app-payment-modal',
	templateUrl: './payment-modal.component.html',
	styleUrls: ['./payment-modal.component.scss'],
})
export class PaymentModalComponent implements OnInit, OnDestroy {
	payment: Payment;
	venue: Venue;
	orders: Order[];
	customer: Customer;
	loading = false;
	paymentMethods: PaymentMethod[] = [];
	selectedPaymentMethod: PaymentMethod = null;
	pm = PaymentMethod;
	vrUrl = environment.baseUrl + PAYMENT_SERVICE + 'vr/pay/';
	orderUtils = OrderUtils;
	environment = environment;
	payments = {};

	isAdyenLoading = false;
	private adyenCustomCard: CustomCard = null;
	wpwlOptionsBase = {
		locale: 'de',
		style: 'plain',
	};
	forceAddressInput: false;
	onPayment: (Payment) => void;
	constructor(
		private modalCtrl: ModalController,
		private analytics: AnalyticsService,
		private snackBarCtrl: MatSnackBar,
		private translate: TranslateService,
		private cdr: ChangeDetectorRef,
		private navService: NavigationService,
		private zone: NgZone
	) {}

	get order(): Order {
		return this.orders[0];
	}

	static async show(
		modalCtrl: ModalController,
		venue: Venue,
		orders: Order[],
		customer: Customer,
		tip: number = 0,
		forceAddressInput: boolean = false,
		onPayment: (Payment) => void
	): Promise<{ result: PaymentModalResult; error: any; payment: Payment }> {
		const paymentMethods: PaymentMethod[] = [];

		if (venue.gpEnabled) {
			paymentMethods.push(PaymentMethod.GPAY);
		}
		if (venue.apEnabled && applePayAvailable) {
			paymentMethods.push(PaymentMethod.APAY);
		}

		if (venue.sbEnabled) {
			paymentMethods.push(PaymentMethod.SOFORTBANKING);
		}

		//TURN OF FOR TABILO
		if (venue.cashEnabled && venue.cashPreorderEnabled) {
			paymentMethods.push(PaymentMethod.CASH);
		}
		if (venue.ccvEnabled) {
			paymentMethods.push(PaymentMethod.CCV);
		}
		if (venue?.adyenEnabled) {
			paymentMethods.push(PaymentMethod.ADYEN);
		} else {
			if (venue.ccEnabled) {
				paymentMethods.push(PaymentMethod.CREDIT_CARD);
			}
		}
		if (venue.adyenPaypalEnabled) {
			paymentMethods.push(PaymentMethod.PAYPAL_ADYEN);
		} else {
			if (venue.ppEnabled) {
				paymentMethods.push(PaymentMethod.PAYPAL);
			}
		}
		const modal = await modalCtrl.create({
			component: PaymentModalComponent,
			componentProps: {
				payment: paymentFromOrders(orders, tip),
				paymentMethods,
				venue,
				orders,
				customer,
				forceAddressInput,
				onPayment,
			},
			backdropDismiss: false,
		});
		await modal.present();
		const result = await modal.onDidDismiss();
		await sleep(100);
		return result.data;
	}

	ngOnInit() {
		this.analytics.paymentModalOpened(this.order);
	}

	private getAdyenLocale(): string {
		const currentLang = this.translate.currentLang || 'de';

		// Adyen only accepts underscored locales (SUPPORTED_LOCALES_EU / _US), not the
		// hyphenated BCP-47 tags ngx-translate uses.
		const localeMapping = {
			de: 'de_DE',
			en: 'en_US',
			fr: 'fr_FR',
			'de-DE': 'de_DE',
			'en-US': 'en_US',
			'fr-FR': 'fr_FR',
		};

		return localeMapping[currentLang] || 'de_DE';
	}

	async loadPayment() {
		if (this.loading) {
			return;
		}
		this.loading = true;
		this.resetJs();
		this.payment._id = null;
		this.payment.method = this.selectedPaymentMethod;
		this.selectedPaymentMethod = null;
		wpwlOptions = null;
		this.cdr.detectChanges();
		this.selectedPaymentMethod = this.payment.method;

		try {
			switch (this.selectedPaymentMethod) {
				case PaymentMethod.PAYPAL:
					await this.loadPaypal();
					break;
				case PaymentMethod.PAYPAL_ADYEN:
					await this.adyenLoadPayPal();
					break;
				case PaymentMethod.CREDIT_CARD:
					await this.loadCreditCard();
					break;
				case PaymentMethod.SOFORTBANKING:
					await this.loadKlarna();
					break;
				case PaymentMethod.GPAY:
					if (this.venue.adyenEnabled) {
						await this.loadGpayAdyen();
					} else {
						await this.loadGpay();
					}
					break;
				case PaymentMethod.APAY:
					if (this.venue.adyenEnabled) {
						await this.loadAdyenApay();
					} else {
						await this.loadApay();
					}
					break;
				case PaymentMethod.CASH:
					await this.loadCashButton();
					break;
				case PaymentMethod.CCV:
					await this.ccvPay(PaymentMethod.CCV);
					break;
				case PaymentMethod.ADYEN:
					await this.paymentMethodAdyen();
					break;
			}
		} catch (e) {
			console.error(e);
			this.snackBarCtrl.open(this.translate.instant('payment_modal.error'));
			this.orders.forEach(order => {
				this.analytics.paymentLoadingFailed(order, this.selectedPaymentMethod);
			});
			this.selectedPaymentMethod = null;
			this.loading = false;
			return false;
		}
		this.loading = false;
		this.orders.forEach(order => {
			this.analytics.paymentLoaded(order, this.selectedPaymentMethod);
		});
	}
	async adyenLoadPayPal() {
		try {
			this.payment.method = PaymentMethod.PAYPAL;
			this.payment.isAdyenPaypal = true;
			this.payment = await Api.createPayment(this.payment);
			this.onPayment(this.payment);
			if (this.payment?.adyenPayment?.redirectUrl) {
				window.location.assign(this.payment?.adyenPayment?.redirectUrl);
			}
		} catch (e) {}
	}
	loadGooglePayScript() {
		const script = document.createElement('script');
		script.src = 'https://pay.google.com/gp/p/js/pay.js';
		script.async = true;
		script.onload = () => this.onGooglePayLoaded();
		document.body.appendChild(script);
	}
	onGooglePayLoaded() {
		const paymentsClient = new google.payments.api.PaymentsClient({ environment: environment.production ? 'PRODUCTION' : 'TEST' });
		console.log('test', this.payment, this.venue);
		const paymentRequest = {
			apiVersion: 2,
			apiVersionMinor: 0,
			allowedPaymentMethods: [
				{
					type: 'CARD',
					parameters: {
						allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
						allowedCardNetworks: ['AMEX', 'DISCOVER', 'JCB', 'MASTERCARD', 'VISA'],
					},
					tokenizationSpecification: {
						type: 'PAYMENT_GATEWAY',
						parameters: {
							gateway: 'adyen',
							gatewayMerchantId: 'BCR2DN6T5OYNT6CG',
						},
					},
				},
			],
			merchantInfo: {
				merchantName: 'Sterlix GmbH',
			},
			transactionInfo: {
				totalPriceStatus: 'FINAL',
				totalPrice: this.payment.sum.toString(),
				currencyCode: 'EUR',
				countryCode: 'DE',
			},
		};

		const button = paymentsClient.createButton({
			onClick: () => this.onGooglePayButtonClicked(paymentsClient, paymentRequest),
		});

		const container = document.getElementById('google-pay-button-container');
		if (container && container.childNodes.length === 0) {
			container.appendChild(button);
		}
	}
	onGooglePayButtonClicked(paymentsClient: any, paymentDataRequest: any) {
		paymentsClient
			.loadPaymentData(paymentDataRequest)
			.then(paymentData => {
				const token = paymentData.paymentMethodData.tokenizationData.token;
				// console.log('Google Pay token:', token);
				this.processPayment(token);
			})
			.catch(err => {
				console.error('loadPaymentData failed:', err);
			});
	}

	processPayment(token: string) {
		this.tryToPayWithGooglePay(token);
	}
	async paymentMethodAdyen() {
		// Only CoreConfiguration options belong here. hasHolderName / holderNameRequired /
		// billingAddressRequired / currency are Card-component options and were silently
		// ignored at this level.
		const checkout = await AdyenCheckout({
			locale: this.getAdyenLocale(),
			environment: environment.production ? 'live' : 'test',
			clientKey: this.venue.adyenCredentials.clientKey,
			countryCode: this.venue?.country?.toUpperCase() || 'DE',
			analytics: {
				enabled: false, //disable analytics cors errors
			},
		});
		this.adyenCustomCard = new CustomCard(checkout, {
			brands: ['mc', 'visa', 'amex', 'bcmc', 'maestro'],
			styles: {
				error: { color: 'red' },
				validated: { color: 'green' },
				placeholder: { color: '#d8d8d8' },
			},
			onError: err => {
				console.error('Adyen custom card error:', err);
			},
		});
		this.adyenCustomCard.mount('#customCard-container');
	}

	// Bound from the template so the handler dies with the view instead of stacking up on
	// #payButton every time a payment method is re-selected.
	submitAdyenCard() {
		const state = this.adyenCustomCard?.state;
		if (!state?.isValid || !state?.data) {
			return;
		}
		const stateData = state.data as Record<string, string>;
		this.startPaymentAdyen({
			card: {
				number: stateData.encryptedCardNumber,
				month: stateData.encryptedExpiryMonth,
				year: stateData.encryptedExpiryYear,
				code: stateData.encryptedSecurityCode,
			},
		});
	}

	private unmountAdyenCustomCard() {
		if (!this.adyenCustomCard) {
			return;
		}
		try {
			this.adyenCustomCard.unmount();
		} catch (e) {
			console.error('Error unmounting Adyen custom card:', e);
		}
		this.adyenCustomCard = null;
	}

	ngOnDestroy() {
		this.unmountAdyenCustomCard();
	}

	async startPaymentAdyen(data: {
		card: {
			number: any;
			month: any;
			year: any;
			code: any;
		};
	}) {
		try {
			this.isAdyenLoading = true;
			this.payment = {
				...this.payment,
				method: PaymentMethod.CREDIT_CARD,
				adyenPayment: data,
			};
			this.payment = await Api.createPayment(this.payment);
			this.onPayment(this.payment);
			if (this.payment.status === PaymentStatus.created) {
				await sleep(4000);
				const paymentData = (await Api.getPayment(this.payment._id)).data;
				this.onPayment(paymentData);
				console.log(paymentData);
				if (paymentData.status === PaymentStatus.done) {
					this.modalCtrl.dismiss();
					await sleep(500);
					await this.navService.paymentSuccess(paymentData.orders[0], paymentData._id);
				}
				if (this.payment?.adyenPayment?.redirectUrl) {
					const form = document.createElement('form');
					form.method = 'POST';
					form.action = this.payment.adyenPayment.redirectUrl;
					const paReqInput = document.createElement('input');
					paReqInput.type = 'hidden';
					paReqInput.name = 'PaReq';
					paReqInput.value = this.payment.adyenPayment.cardAuthRequestPaReq;
					form.appendChild(paReqInput);
					const mdInput = document.createElement('input');
					mdInput.type = 'hidden';
					mdInput.name = 'MD';
					mdInput.value = this.payment.adyenPayment.cardPaymentSessionMd;
					form.appendChild(mdInput);
					document.body.appendChild(form);
					form.submit();
				}
			} else if (this.payment.status === PaymentStatus.done) {
				this.modalCtrl.dismiss();
				await sleep(500);

				await this.navService.paymentSuccess(this.payment.orders[0], this.payment._id);
			} else if (this.payment.status === PaymentStatus.failed) {
				this.snackBarCtrl.open('Sorry payment failed', null, {
					duration: 4000,
				});
			}
			this.isAdyenLoading = false;
		} catch (e) {
			this.isAdyenLoading = false;
			console.log(e);
		}
	}
	async ccvPay(paymentMethod: PaymentMethod) {
		try {
			this.payment = await Api.createCCVPayment(this.venue._id, this.payment);
			this.onPayment(this.payment);
			this.payments[paymentMethod] = JSON.parse(JSON.stringify(this.payment));

			this.loading = false;
			// await this.navigateToPaymentPape(this.payment.walleePayment.url);
		} catch (e) {
			console.error(e, 'error while ccv button setup');
			this.loading = false;
			throw e;
		}
	}
	async loadVr(paymentMethod: PaymentMethod, paramsBuilder: (payment: Payment) => any) {
		try {
			const prevPayment = this.payments[paymentMethod];
			if (prevPayment) {
				console.log({ paymentMethod, wpwl: prevPayment.wpwlOptions });
				this.payment = prevPayment;
				this.onPayment(this.payment);
				wpwlOptions = JSON.parse(JSON.stringify(this.payment.wpwlOptions));
				console.log(wpwlOptions);
				await this.loadVrJs(this.payment.vrPayment.checkoutId);
				return;
			}
			this.payment = await Api.createPayment(this.payment);
			this.onPayment(this.payment);
			wpwlOptions = {
				...this.wpwlOptionsBase,
				...paramsBuilder(this.payment),
			};
			console.log({ paymentMethod, wpwl: wpwlOptions });
			this.payment.wpwlOptions = JSON.parse(JSON.stringify(wpwlOptions));
			this.payments[paymentMethod] = JSON.parse(JSON.stringify(this.payment));

			await this.loadVrJs(this.payment.vrPayment.checkoutId);
		} catch (e) {
			throw e;
		}
	}

	async loadCreditCard() {
		if (!this.venue.vrPaymentEnabled && this.venue.ppFullIntegrationEnabled) {
			await this.loadPaypalCreditCard();
		}
		await this.loadVr(PaymentMethod.CREDIT_CARD, payment => ({
			billingAddress: this.getBillingAddress(this.order, this.customer),
			total: payment.sum,
			mandatoryBillingFields: {
				country: true,
				state: false,
				city: true,
				postcode: true,
				street1: true,
			},
		}));
	}

	async loadGpay() {
		await this.loadVr(PaymentMethod.GPAY, payment => ({
			googlePay: {
				gatewayMerchantId: payment.merchantId,
				allowedAuthMethods: ['PAN_ONLY'],
				allowedCardNetworks: ['MASTERCARD', 'VISA'],
				merchantName: this.venue.name,
				merchantId: environment.GPAY.MERCHANT_ID,
				gateway: environment.GPAY.GATEWAY,
				assuranceDetailsRequired: true,
			},
		}));
	}

	async loadApay() {
		await this.loadVr(PaymentMethod.APAY, payment => ({
			applePay: {
				version: 3,
				displayName: this.venue.name,
				total: this.payment.sum,
				currencyCode: this.payment.currency,
				checkAvailability: 'canMakePayments',
				merchantIdentifier: environment.APAY_MERCHANT_ID,
				style: 'black',
				merchantCapabilities: ['supports3DS'],
				supportedNetworks: ['masterCard', 'visa'],
				shippingType: 'storePickup',
				createCheckout: () => {
					return this.payment.vrPayment.checkoutId;
				},
				onCancel: () => {
					console.log('onCancel');
					this.loading = false;
				},
				onPaymentAuthorized: applePayment => {
					console.log('onPaymentAuthorized payment: ' + JSON.stringify(applePayment));
					// this.payment.status = PaymentStatus.done;
					// this.orders.forEach(order => {
					// 	this.analytics.purchase(order, this.payment);
					// });
					// this.modalCtrl.dismiss({
					// 	result: PaymentModalResult.SUCCESS,
					// 	payment: this.payment,
					// });
				},
			},
		}));
	}

	getBillingAddress(order: Order, customer: Customer): { country: string; city: string; postcode: string; street1: string } {
		if (customer && customer.street) {
			return {
				country: customer.country.toUpperCase(),
				city: customer.city,
				postcode: customer.postalCode,
				street1: customer.street + ' ' + customer.number,
			};
		}
		if (this.forceAddressInput) {
			return {
				country: '',
				city: '',
				postcode: '',
				street1: '',
			};
		}
		return {
			country: order.preorder?.country?.toUpperCase(),
			city: order.preorder?.city,
			postcode: order.preorder?.postalCode,
			street1: order.preorder?.street + ' ' + order.preorder?.number,
		};
	}

	resetJs() {
		this.unmountAdyenCustomCard();
		const head = document.getElementsByTagName('head')[0];
		const prevVrJs = document.getElementById('vr-js');
		if (prevVrJs) {
			head.removeChild(prevVrJs);
		}
		const prevPpJs = document.getElementById('paypal-js');
		if (prevPpJs) {
			head.removeChild(prevPpJs);
		}
	}

	loadVrJs(checkoutId: string): Promise<void> {
		return new Promise<void>(async (resolve, reject) => {
			console.log('load: ' + environment.VR_URL + '?checkoutId=' + checkoutId);
			let widget = null;
			while (!widget) {
				this.cdr.detectChanges();
				await sleep(10);
				widget = document.getElementsByClassName('paymentWidgets')[0];
			}
			const head = document.getElementsByTagName('head')[0];
			const script = document.createElement('script');
			script.type = 'text/javascript';
			script.id = 'vr-js';
			script.src = `${environment.VR_URL}?checkoutId=${checkoutId}`;
			head.appendChild(script);
			script.onerror = () => {
				reject('Could not load script');
				this.loading = false;
			};
			script.onload = async () => {
				this.loading = false;

				resolve();
			};
		});
	}

	loadPaypal(): Promise<any> {
		return this.loadPaypalBase(PaymentMethod.PAYPAL, 'paypal-button');
	}

	loadKlarna(): Promise<any> {
		return this.loadPaypalBase(PaymentMethod.SOFORTBANKING, 'klarna-button');
	}

	loadPaypalCreditCard(): Promise<any> {
		return this.loadPaypalBase(PaymentMethod.CREDIT_CARD, 'paypal-cc-button');
	}

	loadPaypalJs(payment: Payment): Promise<void> {
		return new Promise<void>(async (resolve, reject) => {
			const head = document.getElementsByTagName('head')[0];
			const script = document.createElement('script');
			script.type = 'text/javascript';
			script.id = 'paypal-js';
			script.src = environment.PAYPAL.JS_URL + payment.currency + '&merchant-id=' + payment.merchantId;
			// script.src =
			// 	'https://www.paypal.com/sdk/js?client-id=Ac5vAqN4prOBX3YUFPX_L9Bmz3ncsdmOgkz7LuE2zP3yDbIaJB0y3SD4CTBdJqoHQhcWP6-WMklA-thJ&integration-date=2020-09-04&components=buttons,funding-eligibility&currency=';
			script.setAttribute('data-partner-attribution-id', environment.PAYPAL.BN_CODE);
			head.appendChild(script);
			script.onerror = () => reject('Could not load script');
			script.onload = () => resolve();
		});
	}

	loadPaypalButton(payment: Payment, fundingSource: any, elementId: string): Promise<void> {
		return new Promise<void>(async (resolve, reject) => {
			// @ts-ignore
			const button = paypal.Buttons({
				intent: 'capture',
				commit: true,
				vault: false,
				enableStandardCardFields: true,
				createOrder: (data, actions) => {
					return payment.paypal.orderId;
				},
				onCancel: close => {
					console.log('CANCEL');
					this.modalCtrl.dismiss({
						result: PaymentModalResult.ERROR,
						error: {
							error: PaymentMethod.PAYPAL,
							orderId: this.order._id,
						},
					});
					return;
				},

				onApprove: data => {
					this.finishPaypal(payment, data)
						.then(() => {
							console.log('PAYMENT DONE');
							resolve();
						})
						.catch(err => {
							console.log('ERROR DISMISS');
							reject(err);
						});
				},

				onError: err => {
					this.loading = true;
					console.log('ERRRROR IN PAYPAL PAYMENT');
					this.modalCtrl.dismiss({
						result: PaymentModalResult.ERROR,
						error: err,
					});
				},
				fundingSource,
			});
			if (!button.isEligible()) {
				reject('Button not eligible');
				return;
			}
			button.render('#' + elementId);
			resolve();
		});
	}

	async finishPaypal(payment: Payment, data: any) {
		this.loading = true;
		try {
			const result = await Api.finishPaypalPayment(data.orderID);
			this.onPayment(result);
			console.log('RESULT finish payment');
			// this.orders.forEach(order => {
			// 	this.analytics.purchase(order, result);
			// });
			await this.modalCtrl.dismiss({
				result: PaymentModalResult.SUCCESS,
				payment: result,
			});
		} catch (e) {
			console.log('ERROR IN FINISHED PAYMENT');
			this.orders.forEach(order => {
				this.analytics.paymentFailed(order, payment, 'error finishing paypal ' + e.toString());
			});
			this.loading = false;
			throw e;
		}
	}

	loadPaypalBase(paymentMethod: PaymentMethod, elementId: string): Promise<void> {
		return new Promise<void>(async (resolve, reject) => {
			try {
				const prevPayment = this.payments[paymentMethod];
				if (prevPayment) {
					this.payment = prevPayment;
					this.onPayment(this.payment);
					await this.loadPaypalJs(this.payment);
					// @ts-ignore
					await this.loadPaypalButton(this.payment, this.paymentMethodToPaypalFunding(paymentMethod), elementId);
					resolve();
					return;
				}
				this.payment = await Api.createPayment(this.payment);
				this.onPayment(this.payment);
				this.payments[paymentMethod] = JSON.parse(JSON.stringify(this.payment));

				await this.loadPaypalJs(this.payment);
				// @ts-ignore
				await this.loadPaypalButton(this.payment, this.paymentMethodToPaypalFunding(paymentMethod), elementId);
				resolve();
			} catch (e) {
				console.error('error while paypal setup', e);
				reject(e);
			}
		});
	}

	paymentMethodToPaypalFunding(paymentMethod: PaymentMethod): any {
		switch (paymentMethod) {
			case PaymentMethod.CREDIT_CARD:
				// @ts-ignore
				return paypal.FUNDING.CARD;
			case PaymentMethod.SOFORTBANKING:
				// @ts-ignore
				return paypal.FUNDING.CARD;
			case PaymentMethod.PAYPAL:
				// @ts-ignore
				return paypal.FUNDING.PAYPAL;
			default:
				return null;
		}
	}

	async dismiss() {
		if (this.loading) {
			return;
		}
		if (this.orders[0]?.sanifairVouchers?.length > 0) {
			// try {
			// 	await Api.extendSanifairVoucher(this.orders[0]?._id);
			// } catch (e) {
			// 	this.snackBarCtrl.open('Smth go wrong while extend order...', null, {
			// 		duration: 4000
			// 	});
			// }
		}
		await this.modalCtrl.dismiss({
			result: PaymentModalResult.DISMISS,
		});
	}
	async loadCashButton() {
		this.selectedPaymentMethod = PaymentMethod.CASH;
	}
	async loadCashPay() {
		console.log('Cash Payment');
		this.loading = true;
		this.payment = await Api.createPayment(this.payment);
		this.onPayment(this.payment);
		this.loading = false;
		await this.modalCtrl.dismiss({
			result: PaymentModalResult.SUCCESS,
			payment: this.payment,
		});
	}
	navigateCvv(url: string) {
		window.location.replace(url);
	}
	async tryToPayWithGooglePay(googleToken: string) {
		let token = googleToken;

		try {
			this.payment = {
				...this.payment,
				method: PaymentMethod.GPAY,
				adyenPayment: {
					gpToken: token,
				},
			};
			const response = await Api.createPayment(this.payment);
			this.onPayment(this.payment);
			if (response.status == PaymentStatus.created) {
				await sleep(2500);
				const result = await Api.getPayment(response._id);
				console.log('RESULT PAYMENT', result, result.data.status, PaymentStatus.done, result.data.status === PaymentStatus.done);
				if (result.data.status === PaymentStatus.done) {
					return result.data;
					await this.modalCtrl.dismiss({
						result: PaymentModalResult.SUCCESS,
						payment: result,
					});
				}
			} else if (response.status == PaymentStatus.done) {
				await this.modalCtrl.dismiss({
					result: PaymentModalResult.SUCCESS,
					payment: response,
				});

				return response;
			} else {
				return null;
			}
			return null;
		} catch (e) {
			return null;
		}
	}

	async loadGpayAdyen() {
		try {
			await sleep(500);
			let componentData = null;
			const handlePayment = async (state: any, component: any, action: any) => {
				console.log('state', state);
				if (state?.isValid && state?.data?.paymentMethod?.googlePayToken) {
					console.log('GooglePay token:', state?.data?.paymentMethod?.googlePayToken);

					try {
						const result = await this.tryToPayWithGooglePay(state?.data?.paymentMethod?.googlePayToken);
						console.log('Payment result:', result);
						if (result) {
							action.resolve(result?.adyenPayment?.gpResponse);
						}
					} catch (err) {
						console.error('Payment error:', err);
						component.setStatus('error');
					}
				}
			};

			const handleAdditionalDetails = (state: any, component: any) => {
				// Тут можна обробити 3DS або інші додаткові дані, якщо потрібно
				console.log('Additional details:', state, component);
			};

			// Конфігурація Adyen
			console.log(this.payment.sum);
			const checkout = await AdyenCheckout({
				clientKey: this.venue.adyenCredentials.clientKey,
				environment: environment.production ? 'live' : 'test',
				locale: this.getAdyenLocale(),
				countryCode: this.venue?.country?.toUpperCase() || 'DE',
				amount: {
					// minor units, as a number - Adyen types this as number, not string
					value: Math.round(this.payment.sum * 100),
					currency: 'EUR',
				},
				analytics: {
					enabled: false,
				},
				onSubmit: handlePayment,
				onPaymentCompleted: (result, component) => {
					console.log('Payment completed:', result, component);
				},
				onError: (error, component) => {
					console.error('Payment error:', error);
				},
			});
			// paymentMethodsConfiguration was removed from the checkout config in Web v6 -
			// per-method options belong on the component itself.
			componentData = new GooglePay(checkout, {
				buttonType: 'checkout',
				buttonColor: 'black',
				environment: environment.production ? 'live' : 'test',
				configuration: {
					merchantId: 'BCR2DN6T5OYNT6CG',
					gatewayMerchantId: this.venue.adyenCredentials.merchantAccount,
					merchantName: this.venue.name,
				},
			});

			const isAvailable = await componentData
				.isAvailable()
				.then(() => true)
				.catch(() => false);
			if (!isAvailable) {
				console.warn('Google Pay is not available for this shopper');
				return;
			}
			componentData.mount('#gpay-container');
		} catch (e) {
			console.error('Error loading Google Pay:', e);
		}
	}

	async loadAdyenApay() {
		const initiateApplePay = async () => {
			const paymentRequest = {
				countryCode: 'DE',
				currencyCode: 'EUR',
				environment: environment.production ? 'PRODUCTION' : 'TEST',
				amount: { currency: 'EUR', value: this.payment.sum },
				total: {
					label: 'Merchant',
					amount: this.payment.sum,
					type: 'final',
				},
				clientKey: this.venue.adyenCredentials.clientKey,
				supportedNetworks: ['visa', 'masterCard', 'amex'],
				merchantCapabilities: ['supports3DS'],
				version: 3,
				displayName: this.venue.name,
				checkAvailability: 'canMakePayments',
				merchantIdentifier: environment.production
					? 'merchant.com.adyen.de.smoothr.losteria'
					: 'merchant.com.adyen.de.smoothr.losteria-dev',
				style: 'black',
				shippingType: 'storePickup',
			};
			//@ts-ignore
			const session = new ApplePaySession(3, paymentRequest);

			session.onvalidatemerchant = (event: any) => {
				try {
					this.loading = true;
					Api.applePaySession(this.venue._id, {
						url: event?.validationURL,
						venue: this.venue._id,
						channel: 'web',
						customerGroup: environment.customerGroup,
					})
						.then(result => {
							try {
								session.completeMerchantValidation(result?.data);
							} catch (e) {
								console.log("Error session validateMerchant")
								console.error(e);
								this.loading = false;
							}
						})
						.catch(e => {
							this.loading = false;
							console.error(e);
						});
				} catch (e) {

					this.loading = false;
					console.error(e);
					// session.completePayment(ApplePaySession.STATUS_FAILURE);
				}
			};

			session.onpaymentauthorized = event => {
				const paymentData = event.payment;
				console.log('paymentData', paymentData);
				try {
					try {
						this.payment = {
							...this.payment,
							method: PaymentMethod.APAY,
							adyenPayment: {
								apToken: JSON.stringify(paymentData?.token?.paymentData),
							},
						};
						Api.createPayment(this.payment).then(async v => {
							console.log(session);
							this.onPayment(this.payment);
							try {
								session.completePayment({
									status: ApplePaySession.STATUS_SUCCESS,
								});
								await this.modalCtrl.dismiss({
									result: PaymentModalResult.SUCCESS,
									payment: v,
								});
							} catch (e) {
								console.log(e);
								session.completePayment(ApplePaySession.STATUS_FAILURE);
							}
						});
					} catch (e) {
						session.completePayment(ApplePaySession.STATUS_FAILURE);
					}
				} catch (e) {
					console.log(e);
					//
				}
			};
			session.begin();
		};
		window.addEventListener('error', e => {
			console.error('Global error handler:', e.message, e.error);
		});
		await initiateApplePay();
		await sleep(20000);
	}
}
